/** Leads inbox (S30 to S32) and the events summary. docs/50 §4.3, docs/52 §3.4. */
import { z } from 'zod';
import { Cursor, Day, Email, IsoDateTime, Limit, PhoneE164, PositiveCents, page } from '../common.ts';
import { EventMetric, LeadSource, LeadStatus } from '../enums.ts';
import { LeadId, LocationId, UserId } from '../ids.ts';
import { DeliveryRecord } from './shared.ts';

/** `filtered`: leads the spam rules filed as `spam`, which the owner can open and mark Not spam. Never shown elsewhere. */
export const LeadFilter = z.enum(['all', 'new', 'forms', 'calls', 'won', 'filtered']);

export const LeadListQuery = z
  .object({
    filter: LeadFilter.optional(),
    status: LeadStatus.optional(),
    location: LocationId.optional(),
    cursor: Cursor.optional(),
    limit: Limit.optional(),
  })
  .strict();

export const LeadSummary = z.object({
  id: LeadId,
  /** null for anonymous rows. */
  name: z.string().max(120).nullable(),
  serviceLabel: z.string().max(80).nullable(),
  town: z.string().max(80).nullable(),
  source: LeadSource,
  status: LeadStatus,
  receivedAt: IsoDateTime,
  unread: z.boolean(),
  locationId: LocationId.nullable(),
});

export const CallTapDay = z.object({
  day: Day,
  count: z.number().int().min(0),
  pages: z.array(z.object({ path: z.string().max(512), label: z.string().max(120), count: z.number().int().min(0) })),
});

export const LeadList = z.object({
  items: z.array(LeadSummary),
  nextCursor: Cursor.nullable(),
  /** Grouped anonymous call taps, present for the `all` and `calls` filters (docs/52 S32). */
  callTaps: z.array(CallTapDay),
  /** `filtered` counts spam rows (capped like the others); older servers leave it out. */
  counts: z.object({ new: z.number().int().min(0), total: z.number().int().min(0), filtered: z.number().int().min(0).optional() }),
});

export const LeadDetail = LeadSummary.extend({
  phone: PhoneE164.nullable(),
  email: Email.nullable(),
  message: z.string().max(5000).nullable(),
  pagePath: z.string().max(512).nullable(),
  pageLabel: z.string().max(120).nullable(),
  smsConsent: z.boolean(),
  valueCents: PositiveCents.nullable(),
  assignedUserId: UserId.nullable(),
  firstOpenedAt: IsoDateTime.nullable(),
  contactedAt: IsoDateTime.nullable(),
  deliveries: z.array(DeliveryRecord),
  /** When the contact details are deleted (active client plus 90 days, docs/53 §9). */
  purgeAfter: IsoDateTime,
  canAskForReview: z.boolean(),
});

export const LeadStatusBody = z
  .object({ status: LeadStatus, valueCents: PositiveCents.nullable().optional() })
  .strict();

/** "Job done" on a won lead pre-fills the review request (docs/50 §4.3). Nothing is sent yet. */
export const LeadJobDoneResponse = z.object({
  prefill: z.object({
    leadId: LeadId,
    firstName: z.string().max(60).nullable(),
    phone: PhoneE164.nullable(),
    email: Email.nullable(),
    serviceSlug: z.string().max(64).nullable(),
    locationId: LocationId.nullable(),
  }),
});

export const LeadsCsvQuery = z
  .object({ from: Day.optional(), to: Day.optional(), location: LocationId.optional(), includeSpam: z.enum(['0', '1']).optional() })
  .strict();

export const EventsSummaryQuery = z.object({ from: Day, to: Day, location: LocationId.optional() }).strict();

export const EventsSummary = z.object({
  from: Day,
  to: Day,
  days: z.array(z.object({ day: Day, metrics: z.partialRecord(EventMetric, z.number().int().min(0)) })),
  callTaps: z.array(CallTapDay),
  updatedAt: IsoDateTime.nullable(),
});

export const LeadPage = page(LeadSummary);
